import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { PROBLEM_TYPES, isProblem } from "@jbnl/api-client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckboxCard } from "@/components/CheckboxCard";
import { Checkbox } from "@/components/ui/checkbox";
import { FormActions } from "@jbnl/shared-ui";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { datumLang } from "@/i18n/datum";
import { ReistijdSortering } from "@/components/ReistijdSortering";
import {
  gesorteerdOpReistijd,
  reistijdLabel,
  type Reistijdsortering,
} from "@/lib/reistijd";
import {
  VOORKEUR,
  type Inschrijving,
  type InschrijvingVelden,
  type VoorkeurInput,
} from "./useInschrijving";

/**
 * Two steps because the klassen decide which dagen are on offer: one page left a korps scrolling
 * up to change a klasse and back down to see what moved. Entering and changing take the same
 * body, so this draws both and only the labels differ.
 */
export function PloegWizard({
  id,
  inschrijving,
  velden,
  standaardNaam,
  submitLabel,
  pending,
  error,
  gesloten,
  leadingAction,
  onSubmit,
}: {
  /** Prefixes every control's id, so two of these on one page do not collide. */
  id: string;
  inschrijving: Inschrijving;
  /** What the korps has already said. Absent for a ploeg being entered. */
  velden?: InschrijvingVelden;
  /**
   * The name an empty field gets. The server derives it either way; this is only what the
   * placeholder promises, so it comes from the API rather than being guessed here.
   */
  standaardNaam: string;
  submitLabel: string;
  pending: boolean;
  error: Error | null;
  /**
   * The caller's, not `inschrijving.isOpen`: a beheerder is always standing in, which is who
   * §4.4.4 lets past a deadline (ADR-0031).
   */
  gesloten: boolean;
  /**
   * Sits at the far left of the action row, a whole row away from the submit button on the
   * right. Where withdrawing a ploeg goes - the caller owns it, because entering a ploeg has
   * nothing to withdraw yet.
   */
  leadingAction?: ReactNode;
  onSubmit: (velden: InschrijvingVelden) => void;
}) {
  const { t } = useTranslation(["inschrijving", "common"]);

  // What the korps already said, as this form holds it. Read once: it is both what the fields
  // open on and what "changed since" is measured against, and deriving it twice is two chances
  // for the two to disagree.
  const [begin] = useState(() => beginwaarden(inschrijving, velden));

  const [naam, setNaam] = useState(begin.naam);
  const [klasseIds, setKlasseIds] = useState(begin.klasseIds);
  const [voorkeuren, setVoorkeuren] = useState(begin.voorkeuren);
  const [geenVoorkeuren, setGeenVoorkeuren] = useState(begin.geenVoorkeuren);
  const [samen, setSamen] = useState(begin.samen);
  const [vr, setVr] = useState(begin.vr);

  const [sortering, setSortering] = useState<Reistijdsortering>("datum");

  /**
   * How much this inschrijfperiode asks, as a beheerder configured it. Already resolved against
   * the toggles server-side, so 0 means "does not ask" and there is no second flag to consult.
   */
  const maxVoorkeuren = Number(inschrijving.maxVoorkeuren);
  const maxGeenVoorkeuren = Number(inschrijving.maxGeenVoorkeuren);

  /**
   * With both halves off the tables are dropped rather than drawn empty. Step 2 still exists:
   * the samenvoorkeur is asked of every ploeg whatever the periode configures (ADR-0052).
   */
  const heeftDatumvragen = maxVoorkeuren > 0 || maxGeenVoorkeuren > 0;

  const [stap, setStap] = useState<1 | 2>(1);

  /**
   * The naam is asked on step 1, but the server can only judge it once the whole inschrijving
   * arrives from step 2 - which names are taken depends on the klassen (ADR-0055). So the one
   * refusal the korps has to act on lands two steps from the field that causes it.
   */
  const naamBezet = isProblem(error, PROBLEM_TYPES.ploegnaamInGebruik);

  // Adjusted while rendering rather than in an effect: an effect paints step 2 and then jumps,
  // and React's own guidance is to derive the move from the change that causes it. Keyed on the
  // error object, so submitting a name that is still taken moves them again.
  const [vorigeError, setVorigeError] = useState(error);
  if (error !== vorigeError) {
    setVorigeError(error);
    if (isProblem(error, PROBLEM_TYPES.ploegnaamInGebruik)) {
      setStap(1);
    }
  }

  /**
   * Unticking drops that klasse's answers outright (ADR-0049): they were about that klasse, and
   * the server refuses a voorkeur naming one the ploeg is not in. Ticking adds none.
   */
  function toggleKlasse(klasseId: string) {
    const eraf = klasseIds.includes(klasseId);

    setKlasseIds(
      eraf
        ? klasseIds.filter((id) => id !== klasseId)
        : [...klasseIds, klasseId],
    );

    if (eraf) {
      setVoorkeuren((huidig) => zonder(huidig, klasseId));
      setGeenVoorkeuren((huidig) => zonder(huidig, klasseId));
    }
  }

  function toggleVoorkeur(klasseId: string, dagId: string) {
    setVoorkeuren((huidig) => wissel(huidig, klasseId, dagId));

    // A dag holds one answer within a klasse, so preferring it takes back ruling it out - and
    // only there: the same dag may still be ruled out for another klasse of this ploeg.
    setGeenVoorkeuren((huidig) => verwijder(huidig, klasseId, dagId));
  }

  function toggleGeenVoorkeur(klasseId: string, dagId: string) {
    setGeenVoorkeuren((huidig) => wissel(huidig, klasseId, dagId));
    setVoorkeuren((huidig) => verwijder(huidig, klasseId, dagId));
  }

  /**
   * Narrowed per klasse, not to the union: a ploeg in Aspiranten LD and HD could otherwise rank
   * a dag its HD half can never play - eight of the nine 2026 dagen (ADR-0049).
   */
  const tabellen = useMemo(
    () =>
      inschrijving.klassen
        .filter((klasse) => klasseIds.includes(klasse.id))
        .map((klasse) => ({
          klasse,
          dagen: gesorteerdOpReistijd(
            inschrijving.wedstrijddagen.filter((dag) =>
              dag.klasseIds.includes(klasse.id),
            ),
            sortering,
          ),
        })),
    [inschrijving.klassen, inschrijving.wedstrijddagen, klasseIds, sortering],
  );

  const onbewaard = heeftOnbewaardeWijzigingen(
    { naam, klasseIds, voorkeuren, geenVoorkeuren, samen, vr },
    begin,
  );

  /**
   * Only while something is unsaved: a guard that fires on every reload is one korpsen learn to
   * click through, including the reload that costs them their voorkeuren.
   */
  useEffect(() => {
    if (!onbewaard) return;

    const waarschuw = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", waarschuw);
    return () => window.removeEventListener("beforeunload", waarschuw);
  }, [onbewaard]);

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();

        // Enter on the first step goes forward rather than saving. The submit button is not
        // rendered there, but a form still submits on Enter in a text field - and the naam
        // field is a text field.
        if (stap === 1) {
          if (klasseIds.length > 0) setStap(2);
          return;
        }

        onSubmit({
          name: naam,
          klasseIds,
          wilSamenOpEenDag: samen,
          deeltVrVoertuig: vr,
          // Flattened out of the per-klasse maps, and every row carries the klasse it was
          // answered under. The rank restarts at 1 inside each klasse: two klassen each have a
          // first choice, and there is no order between them (ADR-0049).
          voorkeuren: [
            ...klasseIds.flatMap((klasseId) =>
              (voorkeuren[klasseId] ?? []).map((wedstrijddagId, index) => ({
                klasseId,
                wedstrijddagId,
                kind: VOORKEUR.voorkeur,
                rank: index + 1,
              })),
            ),
            // Rank 0 on every one of them: geen-voorkeur-datums are a set rather than an
            // order, and the server stores 0 whatever is sent.
            ...klasseIds.flatMap((klasseId) =>
              (geenVoorkeuren[klasseId] ?? []).map((wedstrijddagId) => ({
                klasseId,
                wedstrijddagId,
                kind: VOORKEUR.geenVoorkeur,
                rank: 0,
              })),
            ),
          ],
        });
      }}
    >
      {/*
        Just the count. The step's own fieldsets already carry their legends, and naming the
        step here as well printed "Voorkeursdatums" twice in a row on the second one.
      */}
      <p className="text-sm text-muted-foreground">
        {t("wizard.stap", { stap })}
      </p>

      {stap === 1 && (
        <NaamEnKlassenStap
          id={id}
          naam={naam}
          setNaam={setNaam}
          gesloten={gesloten}
          standaardNaam={standaardNaam}
          naamBezet={naamBezet}
          error={error}
          klassen={inschrijving.klassen}
          klasseIds={klasseIds}
          onToggleKlasse={toggleKlasse}
          t={t}
        />
      )}

      {stap === 2 && (
        <VoorkeurenStap
          id={id}
          heeftDatumvragen={heeftDatumvragen}
          sortering={sortering}
          setSortering={setSortering}
          tabellen={tabellen}
          inschrijving={inschrijving}
          voorkeuren={voorkeuren}
          geenVoorkeuren={geenVoorkeuren}
          maxVoorkeuren={maxVoorkeuren}
          maxGeenVoorkeuren={maxGeenVoorkeuren}
          gesloten={gesloten}
          onVoorkeur={toggleVoorkeur}
          onGeenVoorkeur={toggleGeenVoorkeur}
          samen={samen}
          setSamen={setSamen}
          vr={vr}
          setVr={setVr}
          t={t}
        />
      )}

      {/* Destructive at the far left, a whole row from the one you want almost every time. */}
      <FormActions className="border-t pt-4" leading={leadingAction}>
        <WizardActies
          stap={stap}
          klasseIds={klasseIds}
          gesloten={gesloten}
          error={error}
          naamBezet={naamBezet}
          pending={pending}
          submitLabel={submitLabel}
          setStap={setStap}
          t={t}
        />
      </FormActions>
    </form>
  );
}

/** Step 1: the naam field and which klassen this ploeg competes in. */
function NaamEnKlassenStap({
  id,
  naam,
  setNaam,
  gesloten,
  standaardNaam,
  naamBezet,
  error,
  klassen,
  klasseIds,
  onToggleKlasse,
  t,
}: {
  id: string;
  naam: string;
  setNaam: (naam: string) => void;
  gesloten: boolean;
  standaardNaam: string;
  naamBezet: boolean;
  error: Error | null;
  klassen: Inschrijving["klassen"];
  klasseIds: string[];
  onToggleKlasse: (klasseId: string) => void;
  t: TFunction<["inschrijving", "common"]>;
}) {
  return (
    <>
      {/* Optional: the derived name is what JBNL prints, but a korps with two teams knows which
        is which better than "2" does. */}
      <Field>
        <FieldLabel htmlFor={`${id}-naam`}>{t("wizard.naam.label")}</FieldLabel>
        <Input
          id={`${id}-naam`}
          value={naam}
          maxLength={200}
          disabled={gesloten}
          placeholder={standaardNaam}
          onChange={(event) => setNaam(event.target.value)}
          aria-invalid={naamBezet || undefined}
        />
        {naamBezet && error && (
          <p className="text-sm text-destructive">{error.message}</p>
        )}
        <FieldDescription>
          <Trans
            t={t}
            i18nKey="wizard.naam.description"
            values={{ standaard: standaardNaam }}
            components={{ strong: <strong /> }}
          />
        </FieldDescription>
      </Field>

      {/* No "alles selecteren", unlike the house pattern: every klasse at once is a combination
        ADR-0033 always refuses. */}
      <FieldSet disabled={gesloten}>
        <FieldLegend variant="label">{t("wizard.klassen.legend")}</FieldLegend>
        <ul className="grid gap-2 sm:grid-cols-2">
          {klassen.map((klasse) => (
            <li key={klasse.id}>
              <CheckboxCard
                id={`${id}-klasse-${klasse.id}`}
                title={klasse.name}
                checked={klasseIds.includes(klasse.id)}
                disabled={gesloten}
                onCheckedChange={() => onToggleKlasse(klasse.id)}
              />
            </li>
          ))}
        </ul>
        <FieldDescription>{t("wizard.klassen.description")}</FieldDescription>
      </FieldSet>
    </>
  );
}

/** Step 2: the per-klasse voorkeursdatum tables, plus the samenvoorkeur asked of every ploeg. */
function VoorkeurenStap({
  id,
  heeftDatumvragen,
  sortering,
  setSortering,
  tabellen,
  inschrijving,
  voorkeuren,
  geenVoorkeuren,
  maxVoorkeuren,
  maxGeenVoorkeuren,
  gesloten,
  onVoorkeur,
  onGeenVoorkeur,
  samen,
  setSamen,
  vr,
  setVr,
  t,
}: {
  id: string;
  heeftDatumvragen: boolean;
  sortering: Reistijdsortering;
  setSortering: (sortering: Reistijdsortering) => void;
  tabellen: {
    klasse: Inschrijving["klassen"][number];
    dagen: Inschrijving["wedstrijddagen"];
  }[];
  inschrijving: Inschrijving;
  voorkeuren: Record<string, string[]>;
  geenVoorkeuren: Record<string, string[]>;
  maxVoorkeuren: number;
  maxGeenVoorkeuren: number;
  gesloten: boolean;
  onVoorkeur: (klasseId: string, dagId: string) => void;
  onGeenVoorkeur: (klasseId: string, dagId: string) => void;
  samen: boolean;
  setSamen: (samen: boolean) => void;
  vr: boolean;
  setVr: (vr: boolean) => void;
  t: TFunction<["inschrijving", "common"]>;
}) {
  return (
    <>
      {/* Dropped whole where the periode asks no datums: the tables would draw their dagen with
        no column to answer, a calendar pretending to be a question. */}
      {heeftDatumvragen && (
        <>
          {/* One sort for every table: it is a way of reading the calendar, not an answer about
            a klasse. */}
          <div className="flex flex-wrap items-center gap-4">
            <ReistijdSortering waarde={sortering} onKies={setSortering} />
          </div>

          {/* A table per klasse, because a ploeg is planned per klasse (ADR-0049): one table
            applied an answer to klassen the korps was never asked about. */}
          {tabellen.map(({ klasse, dagen }) => (
            <KlasseTabel
              key={klasse.id}
              t={t}
              inschrijving={inschrijving}
              klasse={klasse}
              dagen={dagen}
              voorkeuren={voorkeuren[klasse.id] ?? []}
              geenVoorkeuren={geenVoorkeuren[klasse.id] ?? []}
              maxVoorkeuren={maxVoorkeuren}
              maxGeenVoorkeuren={maxGeenVoorkeuren}
              gesloten={gesloten}
              onVoorkeur={(dagId) => onVoorkeur(klasse.id, dagId)}
              onGeenVoorkeur={(dagId) => onGeenVoorkeur(klasse.id, dagId)}
            />
          ))}
        </>
      )}

      {/* Asked of every ploeg including a korps's first: counting the others would leave that
        one never asked, and sitting out its own group unchosen (ADR-0052). Below the tables
        because the planner reads a voorkeursdatum first. */}
      <Field orientation="horizontal">
        <Switch
          id={`${id}-samen`}
          checked={samen}
          disabled={gesloten}
          onCheckedChange={(aan) => setSamen(aan === true)}
        />
        <FieldContent>
          <FieldLabel htmlFor={`${id}-samen`}>
            {t("wizard.samen.label")}
          </FieldLabel>
          <FieldDescription>{t("wizard.samen.description")}</FieldDescription>
        </FieldContent>
      </Field>

      <Field orientation="horizontal">
        <Switch
          id={`${id}-vr`}
          checked={vr}
          disabled={gesloten}
          onCheckedChange={(aan) => setVr(aan === true)}
        />
        <FieldContent>
          <FieldLabel htmlFor={`${id}-vr`}>{t("wizard.vr.label")}</FieldLabel>
          <FieldDescription>{t("wizard.vr.description")}</FieldDescription>
        </FieldContent>
      </Field>
    </>
  );
}

/** The next/previous/submit row, which differs enough per step that a shared ternary read worse
  than two returns. */
function WizardActies({
  stap,
  klasseIds,
  gesloten,
  error,
  naamBezet,
  pending,
  submitLabel,
  setStap,
  t,
}: {
  stap: 1 | 2;
  klasseIds: string[];
  gesloten: boolean;
  error: Error | null;
  naamBezet: boolean;
  pending: boolean;
  submitLabel: string;
  setStap: (stap: 1 | 2) => void;
  t: TFunction<["inschrijving", "common"]>;
}) {
  return stap === 1 ? (
    <VolgendeStapActie
      klasseIds={klasseIds}
      gesloten={gesloten}
      setStap={setStap}
      t={t}
    />
  ) : (
    <OpslaanActies
      error={error}
      naamBezet={naamBezet}
      pending={pending}
      gesloten={gesloten}
      submitLabel={submitLabel}
      setStap={setStap}
      t={t}
    />
  );
}

/** Step 1's own action: a single "volgende", disabled without a klasse since there would be
  nothing to plan voorkeuren for on step 2. */
function VolgendeStapActie({
  klasseIds,
  gesloten,
  setStap,
  t,
}: {
  klasseIds: string[];
  gesloten: boolean;
  setStap: (stap: 1 | 2) => void;
  t: TFunction<["inschrijving", "common"]>;
}) {
  return (
    <>
      {klasseIds.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {t("wizard.klassen.verplicht")}
        </p>
      )}
      {/* Disabled rather than hidden: a vanished control reads as a screen that failed to
        load. Disabled without a klasse too, since the save would be refused. */}
      <Button
        type="button"
        disabled={gesloten || klasseIds.length === 0}
        onClick={() => setStap(2)}
      >
        {t("common:actions.next")}
      </Button>
    </>
  );
}

/** Step 2's own actions: back to step 1, or submit - the naamconflict has its own place on
  step 1 already, so this only prints an error that is not that one. */
function OpslaanActies({
  error,
  naamBezet,
  pending,
  gesloten,
  submitLabel,
  setStap,
  t,
}: {
  error: Error | null;
  naamBezet: boolean;
  pending: boolean;
  gesloten: boolean;
  submitLabel: string;
  setStap: (stap: 1 | 2) => void;
  t: TFunction<["inschrijving", "common"]>;
}) {
  return (
    <>
      {error && !naamBezet && (
        <p className="text-sm text-destructive">{error.message}</p>
      )}
      <Button type="button" variant="outline" onClick={() => setStap(1)}>
        {t("common:actions.previous")}
      </Button>
      <Button type="submit" disabled={pending || gesloten}>
        {pending ? t("common:actions.busy") : submitLabel}
      </Button>
    </>
  );
}

/**
 * One calendar per klasse, independent of the others because the two are planned separately
 * (ADR-0049). A table, not a CSS grid, which let the headers drift out of line with the boxes.
 * Two answer columns: a dag with neither ticked is possible, which is the absence of a row.
 */
function KlasseTabel({
  t,
  inschrijving,
  klasse,
  dagen,
  voorkeuren,
  geenVoorkeuren,
  maxVoorkeuren,
  maxGeenVoorkeuren,
  gesloten,
  onVoorkeur,
  onGeenVoorkeur,
}: {
  t: TFunction<["inschrijving", "common"]>;
  inschrijving: Inschrijving;
  klasse: Inschrijving["klassen"][number];
  dagen: Inschrijving["wedstrijddagen"];
  voorkeuren: string[];
  geenVoorkeuren: string[];
  maxVoorkeuren: number;
  maxGeenVoorkeuren: number;
  gesloten: boolean;
  onVoorkeur: (dagId: string) => void;
  onGeenVoorkeur: (dagId: string) => void;
}) {
  const regels = regelsVoorKlasse(
    t,
    maxVoorkeuren,
    maxGeenVoorkeuren,
    dagen.length,
  );

  return (
    <FieldSet disabled={gesloten}>
      {/* Names the fieldset rather than heading it, so a screen reader says the klasse with every
        checkbox - the one thing telling two identical tables apart. */}
      <FieldLegend variant="label">
        {t("wizard.voorkeuren.legendKlasse", { klasse: klasse.name })}
      </FieldLegend>

      <KlasseKalenderTabel
        t={t}
        inschrijving={inschrijving}
        klasse={klasse}
        dagen={dagen}
        voorkeuren={voorkeuren}
        geenVoorkeuren={geenVoorkeuren}
        maxVoorkeuren={maxVoorkeuren}
        maxGeenVoorkeuren={maxGeenVoorkeuren}
        gesloten={gesloten}
        onVoorkeur={onVoorkeur}
        onGeenVoorkeur={onGeenVoorkeur}
      />

      <FieldDescription>
        {t("wizard.voorkeuren.description", { regels: regels.join(" ") })}
      </FieldDescription>
    </FieldSet>
  );
}

/** The table itself, split out from its fieldset: a table already nests four deep before its own
  head and body get a say, so the fieldset and the table cannot both carry that on top. */
function KlasseKalenderTabel({
  t,
  inschrijving,
  klasse,
  dagen,
  voorkeuren,
  geenVoorkeuren,
  maxVoorkeuren,
  maxGeenVoorkeuren,
  gesloten,
  onVoorkeur,
  onGeenVoorkeur,
}: {
  t: TFunction<["inschrijving", "common"]>;
  inschrijving: Inschrijving;
  klasse: Inschrijving["klassen"][number];
  dagen: Inschrijving["wedstrijddagen"];
  voorkeuren: string[];
  geenVoorkeuren: string[];
  maxVoorkeuren: number;
  maxGeenVoorkeuren: number;
  gesloten: boolean;
  onVoorkeur: (dagId: string) => void;
  onGeenVoorkeur: (dagId: string) => void;
}) {
  const volleVoorkeuren = voorkeuren.length >= maxVoorkeuren;
  const volleGeenVoorkeuren = geenVoorkeuren.length >= maxGeenVoorkeuren;

  return (
    <div className="overflow-x-auto rounded-md border bg-background">
      <Table>
        <TableHeader>
          <VoorkeurKolomKoppen
            t={t}
            maxVoorkeuren={maxVoorkeuren}
            maxGeenVoorkeuren={maxGeenVoorkeuren}
          />
        </TableHeader>
        <TableBody>
          {dagen.map((dag) => (
            <WedstrijddagRij
              key={dag.id}
              t={t}
              inschrijving={inschrijving}
              klasse={klasse}
              dag={dag}
              voorkeuren={voorkeuren}
              geenVoorkeuren={geenVoorkeuren}
              maxVoorkeuren={maxVoorkeuren}
              maxGeenVoorkeuren={maxGeenVoorkeuren}
              gesloten={gesloten}
              volleVoorkeuren={volleVoorkeuren}
              volleGeenVoorkeuren={volleGeenVoorkeuren}
              onVoorkeur={onVoorkeur}
              onGeenVoorkeur={onGeenVoorkeur}
            />
          ))}

          {dagen.length === 0 && (
            <GeenDagenRij
              t={t}
              klasseName={klasse.name}
              maxVoorkeuren={maxVoorkeuren}
              maxGeenVoorkeuren={maxGeenVoorkeuren}
            />
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/** The header row on its own: two answer columns are dropped rather than disabled, so the row
  that decides that has no room left to also sit under the table and the fieldset. */
function VoorkeurKolomKoppen({
  t,
  maxVoorkeuren,
  maxGeenVoorkeuren,
}: {
  t: TFunction<["inschrijving", "common"]>;
  maxVoorkeuren: number;
  maxGeenVoorkeuren: number;
}) {
  return (
    <TableRow>
      <TableHead>{t("wizard.voorkeuren.kolomDag")}</TableHead>
      {/* On a phone the reistijd moves under the dag, so both answer columns stay on screen. */}
      <TableHead className="hidden w-32 sm:table-cell">
        {t("wizard.voorkeuren.kolomReistijd")}
      </TableHead>
      {/* Dropped rather than disabled: a greyed-out column promises something a korps
        cannot have. */}
      {maxVoorkeuren > 0 && (
        <TableHead className="text-center sm:w-24">
          {t("wizard.voorkeuren.kolomVoorkeur")}
        </TableHead>
      )}
      {maxGeenVoorkeuren > 0 && (
        <TableHead className="text-center sm:w-36">
          {t("wizard.voorkeuren.kolomGeenVoorkeur")}
        </TableHead>
      )}
    </TableRow>
  );
}

/** The empty-klasse row on its own, for the same reason as the header row above it. */
function GeenDagenRij({
  t,
  klasseName,
  maxVoorkeuren,
  maxGeenVoorkeuren,
}: {
  t: TFunction<["inschrijving", "common"]>;
  klasseName: string;
  maxVoorkeuren: number;
  maxGeenVoorkeuren: number;
}) {
  return (
    <TableRow>
      <TableCell
        colSpan={
          2 + (maxVoorkeuren > 0 ? 1 : 0) + (maxGeenVoorkeuren > 0 ? 1 : 0)
        }
        className="text-sm text-muted-foreground"
      >
        {t("wizard.voorkeuren.geenDagen", { klasse: klasseName })}
      </TableCell>
    </TableRow>
  );
}

/**
 * Clamped to this klasse's own dagen rather than the configured count: "kruis hoogstens vijf
 * dagen aan" above two rows tells a korps something it cannot do.
 */
function regelsVoorKlasse(
  t: TFunction<["inschrijving", "common"]>,
  maxVoorkeuren: number,
  maxGeenVoorkeuren: number,
  aantalDagen: number,
) {
  const regels: string[] = [];

  if (maxVoorkeuren > 0) {
    regels.push(
      t("wizard.voorkeuren.regelVoorkeur", {
        count: Math.min(maxVoorkeuren, aantalDagen),
      }),
    );
  }

  if (maxGeenVoorkeuren > 0) {
    regels.push(
      t("wizard.voorkeuren.regelGeenVoorkeur", {
        // One less than the dagen on screen: ruling out every dag this klasse could be played
        // on is refused on save, so promising it here would set a korps up to be turned away.
        count: Math.min(maxGeenVoorkeuren, Math.max(1, aantalDagen - 1)),
      }),
    );
  }

  return regels;
}

/** One dag's row: its own badges and its own two checkboxes, split out so a row's conditionals
  do not pile onto the table's. */
function WedstrijddagRij({
  t,
  inschrijving,
  klasse,
  dag,
  voorkeuren,
  geenVoorkeuren,
  maxVoorkeuren,
  maxGeenVoorkeuren,
  gesloten,
  volleVoorkeuren,
  volleGeenVoorkeuren,
  onVoorkeur,
  onGeenVoorkeur,
}: {
  t: TFunction<["inschrijving", "common"]>;
  inschrijving: Inschrijving;
  klasse: Inschrijving["klassen"][number];
  dag: Inschrijving["wedstrijddagen"][number];
  voorkeuren: string[];
  geenVoorkeuren: string[];
  maxVoorkeuren: number;
  maxGeenVoorkeuren: number;
  gesloten: boolean;
  volleVoorkeuren: boolean;
  volleGeenVoorkeuren: boolean;
  onVoorkeur: (dagId: string) => void;
  onGeenVoorkeur: (dagId: string) => void;
}) {
  const rang = voorkeuren.indexOf(dag.id);
  const isGeenVoorkeur = geenVoorkeuren.includes(dag.id);

  return (
    <TableRow>
      <TableCell>
        <span className="font-medium">{datumLang(dag.date)}</span>
        <VoorkeurBadges t={t} rang={rang} isGeenVoorkeur={isGeenVoorkeur} />
        <span className="block text-xs text-muted-foreground">
          {dag.niveauName} · {dag.organiserendKorpsName} ·{" "}
          {klassenOpDag(t, inschrijving, dag.klasseIds)}
        </span>
        <span className="block text-xs text-muted-foreground sm:hidden">
          {reistijdLabel(t, dag.reistijdSeconds)}
        </span>
      </TableCell>

      <TableCell className="hidden text-sm whitespace-nowrap text-muted-foreground sm:table-cell">
        {reistijdLabel(t, dag.reistijdSeconds)}
      </TableCell>

      {maxVoorkeuren > 0 && (
        <TableCell className="text-center">
          <Checkbox
            checked={rang >= 0}
            disabled={celGeblokkeerd(gesloten, volleVoorkeuren, rang >= 0)}
            // The klasse is in the label, because the same date appears in every
            // table on this page and "Voorkeur voor 15 mei" would be ambiguous
            // three times over.
            aria-label={t("wizard.voorkeuren.vinkVoorkeur", {
              datum: datumLang(dag.date),
              klasse: klasse.name,
            })}
            onCheckedChange={() => onVoorkeur(dag.id)}
          />
        </TableCell>
      )}

      {maxGeenVoorkeuren > 0 && (
        <TableCell className="text-center">
          <Checkbox
            checked={isGeenVoorkeur}
            disabled={celGeblokkeerd(
              gesloten,
              volleGeenVoorkeuren,
              isGeenVoorkeur,
            )}
            aria-label={t("wizard.voorkeuren.vinkGeenVoorkeur", {
              datum: datumLang(dag.date),
              klasse: klasse.name,
            })}
            onCheckedChange={() => onGeenVoorkeur(dag.id)}
          />
        </TableCell>
      )}
    </TableRow>
  );
}

/** Both answers get a badge: a ticked box says which column it is in only if you follow the row
  back to the header. Red for the dag a korps has ruled out, which is the one most worth spotting
  wrongly ticked. */
function VoorkeurBadges({
  t,
  rang,
  isGeenVoorkeur,
}: {
  t: TFunction<["inschrijving", "common"]>;
  rang: number;
  isGeenVoorkeur: boolean;
}) {
  return (
    <>
      {rang >= 0 && (
        <Badge className="ml-2">
          {t("wizard.voorkeuren.badgeVoorkeur", { rang: rang + 1 })}
        </Badge>
      )}
      {isGeenVoorkeur && (
        <Badge variant="destructive" className="ml-2">
          {t("wizard.voorkeuren.badgeGeenVoorkeur")}
        </Badge>
      )}
    </>
  );
}

/**
 * Disabled at the cap rather than pushing the oldest out: a tick past the limit silently
 * dropping the first is a change the korps did not ask for and would not see.
 */
function celGeblokkeerd(gesloten: boolean, vol: boolean, actief: boolean) {
  return gesloten || (vol && !actief);
}

/** Off for a ploeg being entered, which is the answer that asks the planner for nothing. */
function vlaggenVan(velden: InschrijvingVelden | undefined) {
  return {
    samen: velden?.wilSamenOpEenDag ?? false,
    vr: velden?.deeltVrVoertuig ?? false,
  };
}

/**
 * A saved voorkeur for a dag its klasse does not run on is dropped on the way in rather than
 * riding along to the next save. The voorkeuren are an ordered list, not a rank per dag: the rank
 * is the order, so there is no gap at 2, no two firsts and nothing to renumber.
 */
function beginwaarden(
  inschrijving: Inschrijving,
  velden: InschrijvingVelden | undefined,
) {
  const dagenPerKlasse = dagenPerKlasseVan(inschrijving);
  const klasseIds = velden?.klasseIds ?? [];
  const alleVoorkeuren = velden?.voorkeuren ?? [];

  const voorkeuren = voorkeurenPerKlasse(
    alleVoorkeuren,
    klasseIds,
    dagenPerKlasse,
    VOORKEUR.voorkeur,
  );

  return {
    naam: velden?.name ?? "",
    klasseIds,
    voorkeuren: gerangschiktOpRang(voorkeuren, alleVoorkeuren),
    geenVoorkeuren: voorkeurenPerKlasse(
      alleVoorkeuren,
      klasseIds,
      dagenPerKlasse,
      VOORKEUR.geenVoorkeur,
    ),
    ...vlaggenVan(velden),
  };
}

/** Which dagen each klasse is actually run on, so an answer can be checked against its own
  klasse rather than against the union the form used to draw. */
function dagenPerKlasseVan(inschrijving: Inschrijving) {
  return new Map(
    inschrijving.klassen.map((klasse) => [
      klasse.id,
      new Set(
        inschrijving.wedstrijddagen
          .filter((dag) => dag.klasseIds.includes(klasse.id))
          .map((dag) => dag.id),
      ),
    ]),
  );
}

/** Whether a saved voorkeur still counts: the right kind, for a klasse this ploeg still has, on
  a dag that klasse still runs on. */
function voorkeurTelt(
  voorkeur: VoorkeurInput,
  kind: number,
  klasseIds: string[],
  dagenPerKlasse: Map<string, Set<string>>,
) {
  return (
    voorkeur.kind === kind &&
    klasseIds.includes(voorkeur.klasseId) &&
    (dagenPerKlasse.get(voorkeur.klasseId)?.has(voorkeur.wedstrijddagId) ??
      false)
  );
}

function voorkeurenPerKlasse(
  alleVoorkeuren: VoorkeurInput[],
  klasseIds: string[],
  dagenPerKlasse: Map<string, Set<string>>,
  kind: number,
) {
  const perKlasse: Record<string, string[]> = {};

  for (const voorkeur of alleVoorkeuren) {
    if (!voorkeurTelt(voorkeur, kind, klasseIds, dagenPerKlasse)) continue;
    (perKlasse[voorkeur.klasseId] ??= []).push(voorkeur.wedstrijddagId);
  }

  return perKlasse;
}

/** Sorted by rank inside each klasse, because the order is the ranking. The geen-voorkeur rows
  all carry rank 0 and mean it, so sorting them changes nothing. */
function gerangschiktOpRang(
  voorkeuren: Record<string, string[]>,
  alleVoorkeuren: VoorkeurInput[],
) {
  const gerangschikt: Record<string, string[]> = {};

  for (const [klasseId, dagIds] of Object.entries(voorkeuren)) {
    const rang = new Map(
      alleVoorkeuren
        .filter((v) => v.kind === VOORKEUR.voorkeur && v.klasseId === klasseId)
        .map((v) => [v.wedstrijddagId, Number(v.rank)]),
    );

    gerangschikt[klasseId] = [...dagIds].sort(
      (a, b) => (rang.get(a) ?? 0) - (rang.get(b) ?? 0),
    );
  }

  return gerangschikt;
}

/** One klasse's answers dropped whole - what unticking that klasse means. */
function zonder(antwoorden: Record<string, string[]>, klasseId: string) {
  return Object.fromEntries(
    Object.entries(antwoorden).filter(([id]) => id !== klasseId),
  );
}

/** Tick or untick one dag within one klasse, leaving every other klasse alone. */
function wissel(
  antwoorden: Record<string, string[]>,
  klasseId: string,
  dagId: string,
) {
  const huidig = antwoorden[klasseId] ?? [];

  return {
    ...antwoorden,
    [klasseId]: huidig.includes(dagId)
      ? huidig.filter((id) => id !== dagId)
      : [...huidig, dagId],
  };
}

function verwijder(
  antwoorden: Record<string, string[]>,
  klasseId: string,
  dagId: string,
) {
  return {
    ...antwoorden,
    [klasseId]: (antwoorden[klasseId] ?? []).filter((id) => id !== dagId),
  };
}

/** Whether anything the korps can see has changed since the form opened, asked as one question
  so a reload only warns when there is something to lose. */
function heeftOnbewaardeWijzigingen(
  huidig: ReturnType<typeof beginwaarden>,
  begin: ReturnType<typeof beginwaarden>,
) {
  return (
    huidig.naam !== begin.naam ||
    !zelfdeSet(huidig.klasseIds, begin.klasseIds) ||
    !zelfdeAntwoorden(huidig.voorkeuren, begin.voorkeuren, zelfdeVolgorde) ||
    !zelfdeAntwoorden(huidig.geenVoorkeuren, begin.geenVoorkeuren, zelfdeSet) ||
    !zelfdeVlaggen(huidig, begin)
  );
}

function zelfdeVlaggen(
  huidig: ReturnType<typeof vlaggenVan>,
  begin: ReturnType<typeof vlaggenVan>,
) {
  return huidig.samen === begin.samen && huidig.vr === begin.vr;
}

/**
 * A ranking for the voorkeuren, a set for the geen-voorkeur-datums. An empty list and no entry
 * are the same answer, which is why the keys are unioned rather than compared.
 */
function zelfdeAntwoorden(
  a: Record<string, string[]>,
  b: Record<string, string[]>,
  gelijk: (x: string[], y: string[]) => boolean,
) {
  const klassen = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...klassen].every((klasseId) =>
    gelijk(a[klasseId] ?? [], b[klasseId] ?? []),
  );
}

/**
 * The klassen are a set: unticking one and ticking it again is the same answer, so the order
 * they were ticked in is not a change to warn about.
 */
function zelfdeSet(a: string[], b: string[]) {
  return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}

/** The voorkeuren are a ranking, so reordering the same dagen *is* a change. */
function zelfdeVolgorde(a: string[], b: string[]) {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/**
 * Which klassen a wedstrijddag hosts - information, never a rule. It is what lets a korps see
 * that Brandbestrijding sits on its own dag before they pick a voorkeur they cannot have; the
 * rule itself is enforced on the way in and nowhere else.
 */
function klassenOpDag(
  t: TFunction<"inschrijving">,
  inschrijving: Inschrijving,
  klasseIds: string[],
) {
  const namen = inschrijving.klassen
    .filter((klasse) => klasseIds.includes(klasse.id))
    .map((klasse) => klasse.name);

  return namen.length > 0
    ? namen.join(", ")
    : t("wizard.voorkeuren.geenKlassenOpDag");
}
